'use client';

import { useNow, useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SeatLockDto } from '@cineamo/api/models/seat-lock/SeatLockDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { tailwindColors } from '@cineamo/ui/src/helpers/tailwind-config-helper';

import useLocalStorage from '@cineamo/composables/src/hooks/useLocalStorage';

import Countdown, { CountdownApi } from '@cineamo/ui/src/countdown/Countdown';
import { IconSandClock16 } from '@cineamo/ui/src/icons/ic-sandClock';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import CountdownExpiredModal from './CountdownExpiredModal';
import CountdownInfoModal from './CountdownInfoModal';

const LockCountdown: React.FC = () => {
    const t = useTranslations();
    const { track } = useAnalytics();
    const lockCountdownRef = useRef<CountdownApi>(null);
    const {
        items: cartItems,
        refetchSeatLocks,
        dto,
        initEmptyCart,
        total,
        currentStep,
        setCurrentStep: setStep
    } = useCartProvider();
    const [isCompleted, setIsCompleted] = useState(false);
    /** Set when the lock ran out mid-payment, so the cart is cleared once that step is left. */
    const expiredWhilePayingRef = useRef(false);
    const [isCountdownInfoModalOpen, setIsCountdownInfoModalOpen] = useState(false);
    const [isCountdownExpiredModalOpen, setIsCountdownExpiredModalOpen] = useLocalStorage(
        'isCountdownExpiredModalOpen',
        'false'
    );
    const currentTime = useNow({
        updateInterval: 1000
    });

    const resetExpiredCart = useCallback(() => {
        setIsCountdownExpiredModalOpen('true');
        refetchSeatLocks();
        initEmptyCart();
        setStep('01_seat_picking');
    }, [initEmptyCart, refetchSeatLocks, setStep, setIsCountdownExpiredModalOpen]);

    const targetTimestamp = useMemo(() => {
        if (dto?.isPaid === true) {
            return -1;
        }
        const firstSeatLockCartItem = cartItems.find((cartItem) => cartItem._embedded?.product?.type === 'SeatLock');
        if (firstSeatLockCartItem) {
            const expireTimestamp = new Date(
                (firstSeatLockCartItem._embedded?.product as SeatLockDto).expireDatetime
            ).getTime();
            return expireTimestamp > 0 ? expireTimestamp : -1;
        }
        return -1;
    }, [dto, cartItems]);

    const lockCountdownInitialTime = useMemo(() => {
        if (dto?.isPaid === true || targetTimestamp <= 0) {
            return -1;
        }
        const calculatedTime = Math.floor((targetTimestamp - currentTime.getTime()) / 1000);
        return Math.max(-1, calculatedTime);
    }, [dto, targetTimestamp, currentTime]);

    useEffect(() => {
        if (!isCompleted) {
            return;
        }

        // CIN-15323. The countdown used to empty the cart and jump back to seat picking whatever the
        // customer was doing — including while the payment form was open, which is how people were
        // "kicked out during payment". The window covers seat picking, price selection and the whole
        // card entry, and it never restarts, so someone who browsed for four minutes had one minute
        // left to finish a bank redirect.
        //
        // Once a PaymentIntent exists we leave the checkout alone. Nothing here can save the lock:
        // the server released it at the same moment either way, and the payment service already has
        // the authoritative answer — PaymentIntentAmountCapturableUpdatedListener re-checks every
        // lock before anything is captured and cancels with a message when they are gone (CIN-12601).
        // So the customer either completes (the seats were still free) or is told and not charged,
        // instead of losing a half-entered card to a modal.
        //
        // This deliberately does not touch the five minutes themselves. We create the Cinetixx cart
        // without sending an expiry, so their reservation runs on a server-side TTL we neither set
        // nor know; extending ours could claim seats Cinetixx has already released. Changing the TTL
        // needs an answer from Cinetixx first.
        const isPaying = currentStep === '02_payment';

        track('Countdown Expired', {
            cartTotal: total.toString(),
            // Kept so we can see how often the window runs out mid-payment — that number is what
            // decides whether the TTL itself has to change.
            step: currentStep
        });

        if (isPaying) {
            // The countdown fires once, so without this the cart would stay full of dead locks if the
            // customer backs out of payment instead of completing it. Deferred, not skipped.
            expiredWhilePayingRef.current = true;
        } else {
            resetExpiredCart();
        }

        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset completion flag after handling countdown expiry side-effects
        setIsCompleted(false);
    }, [currentStep, isCompleted, resetExpiredCart, total, track]);

    // The deferred half of the above: the lock ran out while the customer was paying and they left
    // that step without a completed payment, so the cart they are looking at now holds nothing the
    // server still has. A finished payment is excluded — those locks became tickets.
    useEffect(() => {
        if (!expiredWhilePayingRef.current || currentStep === '02_payment') {
            return;
        }

        if (currentStep === '03_payment_complete' || dto?.isPaid === true) {
            expiredWhilePayingRef.current = false;
            return;
        }

        expiredWhilePayingRef.current = false;
        resetExpiredCart();
    }, [currentStep, dto?.isPaid, resetExpiredCart]);

    useEffect(() => {
        if (isCountdownInfoModalOpen) {
            track('Countdown Info Modal Opened');
        }
    }, [isCountdownInfoModalOpen, track]);

    useEffect(() => {
        const lockCountdown = lockCountdownRef.current;
        if (!lockCountdown) return;

        if (lockCountdownInitialTime > 0) {
            lockCountdown.start();
        } else {
            lockCountdown.stop();
        }
    }, [lockCountdownInitialTime]);

    const onComplete = useCallback(() => {
        // `Countdown` calls this from inside its own render when the target is already in the past at
        // mount — a lock that expired while the tab sat open, or one restored from a previous visit.
        // Setting state straight away would update this component while another one is rendering, so
        // it is deferred to a microtask and lands after the commit instead.
        queueMicrotask(() => setIsCompleted(true));
    }, []);

    const isHidden = lockCountdownInitialTime <= 0;

    return (
        <>
            <CountdownExpiredModal
                isOpen={isCountdownExpiredModalOpen === 'true'}
                setIsOpen={(value) => setIsCountdownExpiredModalOpen(value.toString())}
            />
            <CountdownInfoModal
                isOpen={isCountdownInfoModalOpen}
                targetTimestamp={targetTimestamp}
                setIsOpen={setIsCountdownInfoModalOpen}
            />

            <button
                className={cn('lg:mx-[16px] py-4 text-center bg-purple-pale leading-[24px] rounded-4', {
                    hidden: isHidden
                })}
                onClick={() => setIsCountdownInfoModalOpen(true)}>
                <IconSandClock16
                    className="inline mb-1 mr-6"
                    fill={tailwindColors.purple.DEFAULT}
                />
                <span className="text-body-bold-14 text-purple tracking-[.7px]">
                    {t('tickets.reserved-for-you')}:&nbsp;
                    <Countdown
                        className="inline-block w-44 text-center"
                        targetTimestamp={targetTimestamp}
                        autoStart={false}
                        onComplete={onComplete}
                        ref={lockCountdownRef}
                    />
                    &nbsp;{t('common.time.minute-short-label')}
                </span>
            </button>
        </>
    );
};

export default LockCountdown;
